// Onboarding wizard shell. A left progress rail + the active step
// panel on the right. Which panel shows is derived from the persisted
// onboarding record (resume-safe) rather than local component state,
// so a reload mid-wizard lands the creator exactly where they left off
// (04_CREATOR_ONBOARDING.md → Failure handling).
//
// Sprint 1 implements steps 2-4 (GitHub → handle → repos). Step 1
// (Google) is already done by the time we get here — App only mounts
// the wizard for a signed-in user. Steps 5-9 (Cloudflare → publish)
// land in Sprint 2 and show as upcoming on the rail.

import { useAuthStore } from "../../store/authStore";
import { useOnboardingStore } from "../../store/onboardingStore";
import { STEP } from "../../lib/storage/onboarding";
import { StepGithub } from "./StepGithub";
import { StepHandle } from "./StepHandle";
import { StepProvision } from "./StepProvision";
import { StepPublish } from "./StepPublish";
import { StepCloudflare } from "./StepCloudflare";

type RailStatus = "done" | "active" | "upcoming";

interface RailItem {
  n: number;
  label: string;
}

const RAIL: RailItem[] = [
  { n: 1, label: "Sign in with Google" },
  { n: 2, label: "Connect GitHub" },
  { n: 3, label: "Pick your handle" },
  { n: 4, label: "Create your repos" },
  { n: 5, label: "Write your first post" },
  { n: 6, label: "Connect Cloudflare" },
  { n: 7, label: "You're live" },
];

export function Wizard() {
  const user = useAuthStore((s) => s.user)!;
  const loading = useOnboardingStore((s) => s.loading);
  const record = useOnboardingStore((s) => s.record);
  const githubIdentity = useOnboardingStore((s) => s.githubIdentity);
  const reset = useOnboardingStore((s) => s.reset);

  const step = record?.step ?? STEP.NONE;

  // Which panel to render — derived, token-aware (a revoked token sends
  // the creator back to the GitHub step regardless of saved progress).
  let panel: "github" | "handle" | "provision" | "publish" | "cloudflare";
  if (!githubIdentity) panel = "github";
  else if (!record?.handle) panel = "handle";
  else if (step < STEP.REPOS) panel = "provision";
  else if (step < STEP.CF_CONNECTED) panel = "publish";
  else panel = "cloudflare";

  const currentN =
    panel === "github" ? 2 : panel === "handle" ? 3 : panel === "provision" ? 4 : panel === "publish" ? 5 : 6;

  // How far the rail is filled in. Steps past GitHub only count as done
  // once we actually hold a GitHub identity (a revoked token unwinds
  // progress visually, matching where the panel sends the creator).
  let doneThrough = 1; // Google: always done by the time we're here
  if (githubIdentity) {
    doneThrough = 2;
    if (record?.handle) doneThrough = 3;
    if (step >= STEP.REPOS) doneThrough = 4;
    if (step >= STEP.CF_CONNECTED) doneThrough = 5;
    if (step >= STEP.PUBLISHED) doneThrough = 7;
  }

  const railStatus = (n: number): RailStatus => {
    if (n <= doneThrough) return "done";
    if (n === currentN) return "active";
    return "upcoming";
  };

  return (
    <div className="wiz-root">
      <aside className="wiz-rail">
        <div className="wiz-brand">BKA</div>
        <div className="wiz-rail-sub">set up your platform</div>
        <ol className="wiz-steps">
          {RAIL.map((item) => {
            const st = railStatus(item.n);
            return (
              <li key={item.n} className={`wiz-step wiz-step-${st}`}>
                <span className="wiz-step-dot">{st === "done" ? "✓" : item.n}</span>
                <span className="wiz-step-label">{item.label}</span>
              </li>
            );
          })}
        </ol>
        <div className="wiz-rail-foot">
          <span>signed in as <strong>{user.name.split(/\s+/)[0]}</strong></span>
          <button
            className="wiz-reset"
            onClick={() => { if (confirm("Start setup over? This clears your progress here (your GitHub repos stay).")) void reset(user.sub); }}
          >having trouble? start over</button>
        </div>
      </aside>

      <main className="wiz-panel">
        {loading
          ? <div className="wiz-loading">loading your progress…</div>
          : panel === "github" ? <StepGithub />
          : panel === "handle" ? <StepHandle />
          : panel === "provision" ? <StepProvision />
          : panel === "publish" ? <StepPublish />
          : <StepCloudflare />}
      </main>

      <Styles />
    </div>
  );
}

function Styles() {
  return (
    <style>{`
      .wiz-root {
        position: fixed; inset: 0;
        display: grid;
        grid-template-columns: 300px 1fr;
        background: var(--paper);
      }
      .wiz-rail {
        background: var(--paper-raised);
        border-right: 1px solid var(--line);
        padding: var(--space-6) var(--space-5);
        display: flex; flex-direction: column;
      }
      .wiz-brand {
        font-family: var(--font-serif);
        font-size: 28px; font-weight: 600;
        color: var(--teal);
        letter-spacing: 0.04em;
      }
      .wiz-rail-sub {
        font-size: 12px; color: var(--ink-faint);
        text-transform: uppercase; letter-spacing: 0.16em;
        margin-top: 2px;
      }
      .wiz-steps {
        list-style: none; margin: var(--space-6) 0 0; padding: 0;
        display: flex; flex-direction: column; gap: var(--space-1);
        flex: 1;
      }
      .wiz-step {
        display: flex; align-items: center; gap: var(--space-3);
        padding: var(--space-2) var(--space-2);
        border-radius: var(--radius-1);
        font-size: 13px;
      }
      .wiz-step-dot {
        flex: none;
        width: 24px; height: 24px;
        border-radius: 50%;
        display: inline-flex; align-items: center; justify-content: center;
        font-size: 12px; font-weight: 600;
        border: 1px solid var(--line-strong);
        color: var(--ink-faint);
        background: var(--paper);
      }
      .wiz-step-done .wiz-step-dot {
        background: var(--teal); color: #fff; border-color: var(--teal);
      }
      .wiz-step-active { background: var(--teal-dim); }
      .wiz-step-active .wiz-step-dot {
        border-color: var(--teal); color: var(--teal);
      }
      .wiz-step-active .wiz-step-label { color: var(--ink); font-weight: 600; }
      .wiz-step-label { color: var(--ink-soft); display: flex; align-items: center; gap: 8px; }
      .wiz-step-upcoming .wiz-step-label { color: var(--ink-faint); }
      .wiz-soon {
        font-family: var(--font-mono); font-size: 9px;
        text-transform: uppercase; letter-spacing: 0.08em;
        color: var(--teal);
        background: var(--teal-dim);
        padding: 1px 6px; border-radius: 999px;
      }
      .wiz-rail-foot {
        font-size: 12px; color: var(--ink-faint);
        border-top: 1px solid var(--line);
        padding-top: var(--space-3);
        display: flex; flex-direction: column; gap: var(--space-2);
      }
      .wiz-rail-foot strong { color: var(--ink-soft); }
      .wiz-reset {
        align-self: flex-start; background: none; border: none; padding: 0;
        font-family: var(--font-sans); font-size: 11px; color: var(--ink-faint);
        text-decoration: underline; cursor: pointer;
      }
      .wiz-reset:hover { color: var(--rust); }
      .wiz-panel {
        overflow: auto;
        display: flex; align-items: flex-start; justify-content: center;
        padding: var(--space-7) var(--space-6);
      }
      .wiz-loading {
        font-family: var(--font-mono); font-size: 12px;
        color: var(--ink-faint); font-style: italic; margin-top: 12vh;
      }

      /* shared step-panel chrome (used by the Step* components) */
      .step {
        width: min(560px, 100%);
        margin-top: 6vh;
      }
      .step-kicker {
        font-family: var(--font-mono); font-size: 11px;
        text-transform: uppercase; letter-spacing: 0.12em;
        color: var(--teal);
      }
      .step-title {
        font-family: var(--font-serif);
        font-size: 30px; font-weight: 600; color: var(--ink);
        margin: var(--space-2) 0 var(--space-3);
      }
      .step-why {
        font-family: var(--font-serif);
        font-size: 16px; line-height: 1.65; color: var(--ink-soft);
        margin: 0 0 var(--space-5);
      }
      .step-why em { color: var(--ink); font-style: italic; }
      .step-field { margin-bottom: var(--space-4); }
      .step-label {
        display: block; font-size: 12px; font-weight: 600;
        color: var(--ink-soft); margin-bottom: var(--space-2);
      }
      .step-input {
        width: 100%; box-sizing: border-box;
        padding: 10px 12px;
        font-family: var(--font-mono); font-size: 13px;
        background: var(--paper-raised);
        border: 1px solid var(--line-strong);
        border-radius: var(--radius-1);
        color: var(--ink);
      }
      .step-input:focus { outline: none; border-color: var(--teal); }
      .step-hint { font-size: 12px; color: var(--ink-faint); margin-top: 6px; }
      .step-hint code {
        font-family: var(--font-mono);
        background: var(--paper-sunk); padding: 1px 5px; border-radius: 4px;
        color: var(--ink-soft);
      }
      .step-actions {
        display: flex; align-items: center; gap: var(--space-3);
        margin-top: var(--space-5);
      }
      .step-btn {
        font-family: var(--font-sans); font-size: 14px; font-weight: 600;
        padding: 10px 20px; border-radius: var(--radius-1);
        border: 1px solid var(--teal);
        background: var(--teal); color: #fff;
        cursor: pointer;
      }
      .step-btn:hover { background: var(--teal-bright); }
      .step-btn:disabled { opacity: 0.5; cursor: not-allowed; }
      .step-btn-ghost {
        background: transparent; color: var(--teal);
      }
      .step-btn-ghost:hover { background: var(--teal-dim); }
      .step-link {
        font-size: 13px; color: var(--teal); cursor: pointer;
        background: none; border: none; padding: 0; text-decoration: underline;
      }
      .step-error {
        margin-top: var(--space-4);
        padding: var(--space-3);
        background: var(--rust-bg);
        border: 1px solid rgba(180,80,42,0.30);
        border-radius: var(--radius-1);
        color: var(--rust);
        font-size: 13px; line-height: 1.5;
        user-select: text;
      }
      .step-error-title {
        font-family: var(--font-mono); font-size: 10px;
        text-transform: uppercase; letter-spacing: 0.06em;
        margin-bottom: 4px;
      }
      .step-ok {
        font-size: 13px; color: var(--teal); margin-top: 6px;
      }
    `}</style>
  );
}
